import * as React from "react";
import { useRouter } from "next/navigation";
import { DropdownMenuItem, Icon, IconName, P3 } from "@focal/ui-kit";
import { cn, useAnalytics } from "@/lib";
import { useAddMeetingResetStore } from "@/store";

interface NavMenuDropdownItemProps {
  icon?: IconName;
  title: string;
  url: string;
  subtitle?: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  dataAttr?: string;
  dataText?: string;
  /**
   * When true, signals the destination page to reset to a fresh state on
   * click. Needed because navigating to the route you're already on is a no-op
   * in the App Router, leaving the page with stale internal state.
   */
  resetOnNavigate?: boolean;
}

export const NavMenuDropdownItem = ({
  icon,
  title,
  url,
  subtitle,
  onClick,
  className,
  disabled = false,
  dataAttr,
  dataText,
  resetOnNavigate = false,
}: NavMenuDropdownItemProps) => {
  const router = useRouter();
  const { track } = useAnalytics();
  const requestReset = useAddMeetingResetStore((s) => s.requestReset);

  return (
    <DropdownMenuItem
      onSelect={() => {
        if (dataAttr) {
          track(dataAttr, dataText ? { event_text: dataText } : undefined);
        }
        if (resetOnNavigate) {
          requestReset(url);
        }
        onClick ? onClick() : router.push(url);
      }}
      className={cn(
        "flex flex-col justify-center items-start text-primary p-2 cursor-pointer hover:bg-actions-hover rounded-lg",
        className,
      )}
      disabled={disabled}
    >
      <div className="flex items-center mb-1 text-sm font-medium">
        {icon ? (
          <div className="flex h-6 w-6 mr-2 justify-center items-center">
            <Icon name={icon} size="md" />
          </div>
        ) : (
          <></>
        )}

        {title}
      </div>

      {subtitle && (
        <P3 className={cn("text-typography-secondary", icon && "pl-8")}>
          {subtitle}
        </P3>
      )}
    </DropdownMenuItem>
  );
};
